export interface Movie {
  id: string;
  name: string;
  description: string;
  notes: string;
  width: number;
  height: number;
  fps: number;
  scenes: Scene[];
}
export interface MovieSummary {
  id: number;
  movieId?: number;
  name?: string;
  description?: string;
  width?: number;
  height?: number;
  fps?: number;
  sceneCount?: number;
  sceneObjectCount?: number;
  layerCount?: number;
  duration?: number;
  resolution?: string;
  format?: string;
  type?: string;
  isPublic?: boolean;
  isPrivate?: boolean;
  isLocked?: boolean;
  isVisible?: boolean;
  isSample?: boolean;
  tags?: string[];
  coverImage?: string;
  thumbnail?: string;
  fileName?: string;
  lastOpenedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Scene {
  id: string;
  name: string;
  order: number;
  objects: SceneObject[];
}

export interface SceneSummary {
  id: number;
  movieId: number;
  name?: string;
  description?: string;
  sceneDescription?: string;
  framesTotal: number;
  sorter: number;
  createdAt: string;
  updatedAt: string;
}

export interface SceneObjectSummary {
  id: number;
  movieId: number;
  sceneId: number;
  objectName?: string;
  objectType: SceneObjectType;
  objectSrc?: string;
  objectX: number;
  objectY: number;
  objectWidth: number;
  objectHeight: number;
  objectRotation: number;
  objectOpacity: number;
  objectZIndex: number;
  objectVisible: boolean;
  objectLocked: boolean;
}


export type SceneObjectType = 'image' | 'text' | 'shape' | 'clip' | 'group';
export type ShapeType = 'rectangle' | 'circle' | 'triangle' | 'path';

export type StrokeSide = 'top' | 'right' | 'bottom' | 'left';

export type StrokeSides = {
  top: boolean;
  right: boolean;
  bottom: boolean;
  left: boolean;
};

export const ALL_STROKE_SIDES: StrokeSides = {
  top: true,
  right: true,
  bottom: true,
  left: true,
};

export type FillMode = 'solid' | 'gradient' | 'none';

export type FillGradient = {
  angle: number;
  colorStart: string;
  colorEnd: string;
};

export type ObjectShadow = {
  enabled: boolean;
  offsetX: number;
  offsetY: number;
  blur: number;
  spread: number;
  color: string;
};

export type ObjectGlow = {
  enabled: boolean;
  blur: number;
  color: string;
};

export const DEFAULT_FILL_GRADIENT: FillGradient = {
  angle: 180,
  colorStart: '#3b82f6',
  colorEnd: '#8b5cf6',
};

export const DEFAULT_OBJECT_SHADOW: ObjectShadow = {
  enabled: false,
  offsetX: 4,
  offsetY: 4,
  blur: 8,
  spread: 0,
  color: 'rgba(0,0,0,0.35)',
};

export const DEFAULT_OBJECT_GLOW: ObjectGlow = {
  enabled: false,
  blur: 16,
  color: '#60a5fa',
};

/** Sunucudaki defaultState (keyFrameId 0) ile uyumlu dinlenme pozu; timeline scrub ile güncellenmez. */
export interface SceneObjectDefaultPose {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
}

export interface SceneObject {
  id: string;
  type: SceneObjectType;
  name: string;
  src?: string;
  libraryObjectId?: number;
  /** Nested hierarchy — parent scene object id (stringified), null/undefined = root */
  parentObjectId?: string | null;
  /** Explicit group container (empty wrapper or marked sprite). */
  isGroup?: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
  zIndex: number;
  visible: boolean;
  locked: boolean;
  /**
   * Transform origin / joint pivot.
   * Named (`center`, `top-left`, …) or free normalized `"ox,oy"` (0–1).
   */
  pivotPoint?: string;
  layerId?: number; // Timeline layer ID this object belongs to
  /** Keyframe yokken veya o karede bu nesne için state yokken kullanılır (DB defaultState ile aynı). */
  defaultPose?: SceneObjectDefaultPose;
  states?: ObjectState[];
  // Shape properties
  shapeType?: ShapeType;
  pathData?: string; // SVG path d attribute for curves
  fill?: string;
  fillMode?: FillMode;
  fillGradient?: FillGradient;
  stroke?: string;
  strokeWidth?: number;
  strokeStyle?: 'solid' | 'dashed' | 'dotted' | 'none';
  /** Per-side border visibility (CSS box shapes). Undefined = all sides on. */
  strokeSides?: StrokeSides;
  shadow?: ObjectShadow;
  glow?: ObjectGlow;
  // Text properties
  fontSize?: number;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
}

export interface ObjectState {
  frame: number;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
}

export interface LibraryItem {
  id: string;
  name: string;
  type: 'image' | 'clip';
  src: string;
  thumbnail?: string;
}

export interface ClipFolder {
  id: string;
  name: string;
  items: LibraryItem[];
}
